import { useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { DsButton, DsInput, DsModal, DsTextarea } from '@/modules/internal-v2/ds';
import {
  useCreateRole,
  usePermissionCatalog,
  useUpdateRole,
} from '@/api/roles/roles.hooks';
import { BusinessPartnerScope, Role } from '@/api/roles/roles.types';

interface RoleEditorProps {
  role: Role | null;
  onClose: () => void;
}

function errorMessage(error: AxiosError): string {
  return (
    (error.response?.data as { message?: string })?.message ??
    'Speichern fehlgeschlagen.'
  );
}

export function RoleEditor({ role, onClose }: RoleEditorProps) {
  const { data: catalog } = usePermissionCatalog();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const readOnly = role?.isSystem ?? false;

  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [scope, setScope] = useState<BusinessPartnerScope>(
    role?.businessPartnerScope ?? 'ALL',
  );
  const [permissions, setPermissions] = useState<Set<string>>(
    new Set(role?.permissions ?? []),
  );
  const [error, setError] = useState<string | null>(null);

  const isSaving = createRole.isPending || updateRole.isPending;
  const selectedCount = permissions.size;
  const totalCount = useMemo(
    () =>
      (catalog ?? []).reduce((sum, group) => sum + group.permissions.length, 0),
    [catalog],
  );

  const togglePermission = (permission: string) => {
    if (readOnly) return;
    setPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(permission)) {
        next.delete(permission);
      } else {
        next.add(permission);
      }
      return next;
    });
  };

  const toggleGroup = (groupPermissions: string[], allSelected: boolean) => {
    if (readOnly) return;
    setPermissions((prev) => {
      const next = new Set(prev);
      groupPermissions.forEach((permission) => {
        if (allSelected) {
          next.delete(permission);
        } else {
          next.add(permission);
        }
      });
      return next;
    });
  };

  const handleSave = () => {
    setError(null);
    if (!name.trim()) {
      setError('Name ist erforderlich.');
      return;
    }
    const body = {
      name: name.trim(),
      description: description.trim() || undefined,
      businessPartnerScope: scope,
      permissions: [...permissions],
    };
    if (role) {
      updateRole.mutate(
        { id: role.id, body },
        { onSuccess: onClose, onError: (e) => setError(errorMessage(e)) },
      );
    } else {
      createRole.mutate(body, {
        onSuccess: onClose,
        onError: (e) => setError(errorMessage(e)),
      });
    }
  };

  return (
    <DsModal
      open
      onClose={onClose}
      title={role ? role.name : 'Neue Rolle'}
      description={
        readOnly
          ? 'Systemrolle — kann nicht bearbeitet werden.'
          : `${selectedCount} von ${totalCount} Berechtigungen ausgewählt`
      }
      footer={
        <>
          <DsButton onClick={onClose}>
            {readOnly ? 'Schließen' : 'Abbrechen'}
          </DsButton>
          {!readOnly && (
            <DsButton variant="primary" disabled={isSaving} onClick={handleSave}>
              {isSaving ? 'Speichert…' : 'Speichern'}
            </DsButton>
          )}
        </>
      }
    >
      <div className="role-form">
        {error && <div className="ds-alert neg role-form-error">{error}</div>}

        <label className="role-field">
          <span className="role-field-label">Name</span>
          <DsInput
            value={name}
            disabled={readOnly}
            placeholder="z. B. Vertrieb Innendienst"
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label className="role-field">
          <span className="role-field-label">Beschreibung</span>
          <DsTextarea
            value={description}
            disabled={readOnly}
            rows={2}
            placeholder="Wofür ist diese Rolle gedacht?"
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <div className="role-field">
          <span className="role-field-label">Datensichtbarkeit Geschäftspartner</span>
          <div className="role-scope">
            <button
              type="button"
              disabled={readOnly}
              className={`role-scope-option${scope === 'ALL' ? ' active' : ''}`}
              onClick={() => setScope('ALL')}
            >
              <strong>Alle Geschäftspartner</strong>
              <span>Sieht jeden Geschäftspartner</span>
            </button>
            <button
              type="button"
              disabled={readOnly}
              className={`role-scope-option${scope === 'OWN' ? ' active' : ''}`}
              onClick={() => setScope('OWN')}
            >
              <strong>Eigene Geschäftspartner</strong>
              <span>Nur zugewiesene Geschäftspartner</span>
            </button>
          </div>
        </div>

        <div className="role-field">
          <span className="role-field-label">Berechtigungen</span>
          <div className="role-perms">
            {(catalog ?? []).map((group) => {
              const groupPermissions = group.permissions.map(
                (p) => p.permission,
              );
              const allSelected = groupPermissions.every((p) =>
                permissions.has(p),
              );
              return (
                <div key={group.key} className="role-perm-group">
                  <div className="role-perm-group-header">
                    <span>{group.label}</span>
                    <button
                      type="button"
                      disabled={readOnly}
                      className="role-perm-group-toggle"
                      onClick={() => toggleGroup(groupPermissions, allSelected)}
                    >
                      {allSelected ? 'Alle abwählen' : 'Alle auswählen'}
                    </button>
                  </div>
                  {group.permissions.map((definition) => (
                    <label
                      key={definition.permission}
                      className="role-perm-item"
                    >
                      <input
                        type="checkbox"
                        disabled={readOnly}
                        checked={permissions.has(definition.permission)}
                        onChange={() =>
                          togglePermission(definition.permission)
                        }
                      />
                      <span className="role-perm-text">
                        <span className="role-perm-name">
                          {definition.label}
                        </span>
                        <span className="role-perm-desc">
                          {definition.description}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </DsModal>
  );
}
